import React from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

export default function EditQuestionForm({
  formData,
  handleInputChange,
  saveChanges, // Save changes handler
  setEditingQuestion, // For canceling edit
  addQuestion, // Handler for adding a new question
}) {
  const handleAddQuestion = async () => {
    try {
      const response = await fetch("/api/admin/questions", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          model: formData.model,
          questionData: formData,
        }),  
      });

      const data = await response.json();

      if (response.ok) {
        // Display success toast notification
        toast.success("Question added successfully!", {
          position: "top-right",
          autoClose: 3000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
        // Optionally reset the form data after successful addition
        // resetForm();
      } else {
        // Display error toast notification
        toast.error(`Error adding question: ${data.error}`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
        });
      }
    } catch (error) {
      toast.error(`Server error: ${error.message}`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: true,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
      });
    }
  };

  return (
    <div className="mt-6 p-6 bg-gray-50 border border-gray-300 rounded-lg shadow-lg">
      {/* Toast Container for notifications */}
      <ToastContainer />
      
      <h3 className="text-lg font-semibold text-gray-800 mb-4">Add Question</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Model Dropdown */}
        <div className="col-span-2">
          <label className="block text-sm font-medium text-gray-700">Select Model</label>
          <select
            name="model"
            value={formData.model || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          >
            <option value="">Select Model</option>
            <optgroup label="Speaking Models">
              <option value="readaloud">Read Aloud</option>
              <option value="repeatsentence">Repeat Sentence</option>
              <option value="describeimage">Describe Image</option>
              <option value="retelllecture">Retell Lecture</option>
              <option value="answershortquestion">Answer Short Question</option>
            </optgroup>
            <optgroup label="Writing Models">
              <option value="summarizetext">Summarize Text</option>
              <option value="writeessay">Write Essay</option>
            </optgroup>
            <optgroup label="Reading Models">
              <option value="singleanswer">Single Answer</option>
              <option value="doubleanswer">Double Answer</option>
              <option value="reorderparagraph">Reorder Paragraph</option>
              <option value="fillintheblank">Fill in the Blank</option>
            </optgroup>
            <optgroup label="Listening Models">
              <option value="summarizespokentext">Summarize Spoken Text</option>
              <option value="writefromdictation">Write from Dictation</option>
              <option value="highlightsummary">Highlight Correct Summary</option>
              <option value="missingword">Select Missing Word</option>
              <option value="incorrectword">Highlight Incorrect Word</option>
            </optgroup>
          </select>
        </div>

        {/* ID */}
        <div>
          <label className="block text-sm font-medium text-gray-700">ID*</label>
          <input
            type="text"
            name="id"
            value={formData.id || ""} // Update the formData with the id field
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Q Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Q Title</label>
          <input
            type="text"
            name="q_title"
            value={formData.q_title || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Title</label>
          <input
            type="text"
            name="title"
            value={formData.title || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Media Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Media Type</label>
          <input
            type="text"
            name="media_type"
            value={formData.media_type || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Media Link */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Media Link</label>
          <input
            type="text"
            name="media_link"
            value={formData.media_link || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Image Link */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Image Link</label>
          <input
            type="text"
            name="image_link"
            value={formData.image_link || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Question */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Question</label>
          <textarea
            name="question"
            value={formData.question || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
            rows="4"
          />
        </div>

        {/* Answer */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Answer</label>
          <input
            type="text"
            name="answer"
            value={formData.answer || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Audio Script */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Audio Script</label>
          <textarea
            name="audio_script"
            value={formData.audio_script || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
            rows="4"
          />
        </div>

        {/* Complexity */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Complexity</label>
          <input
            type="number"
            name="complexity"
            value={formData.complexity || ""}
            onChange={handleInputChange}
            className="w-full p-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:border-blue-500"
          />
        </div>

        {/* Is Weekly Prediction */}
        <div>
          <label className="block text-sm font-medium text-gray-700">Is Weekly Prediction</label>
          <input
            type="checkbox"
            name="is_weekly_prediction"
            checked={formData.is_weekly_prediction === 1} // Check if it's 1 (true)
            onChange={handleInputChange}
            className="w-6 h-6"
          />
        </div>

        {/* Submit Button */}
        <div className="col-span-2 flex justify-end mt-4">
          <button
            onClick={handleAddQuestion}
            className="px-6 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500"
          >
            Add Question
          </button>
        </div>
      </div>
    </div>
  );
}
